import {
  Grid,
  Button,
  Typography,
  Card,
  FormLabel,
  Autocomplete,
  TextField,
} from "@mui/material";
import BasicTextField from "./formElements/BasicTextField";
import CheckBoxSection from "./formElements/CheckBoxSection";
import SelectInputField from "./formElements/SelectInputField";
import DateField from "./formElements/DateField";
import { useEffect, useState } from "react";
import SplitLayout from "./formLayouts/SplitLayout";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import useButtonStyles from "../../styles/useButtonStyles";
import { useAppDispatch } from "../../redux/hooks";
import { Link, useNavigate } from "react-router-dom";
import { initialFormValuesNew } from "../../static-models/MemberFormData";
import useFormStyles from "../../styles/useFormStyles";
import axios from "axios";
import { APIConstants } from "../../constants/ApiConstants";
import BasicModal from "../ui/BasicModal";
import Loader from "../ui/Loader";
import MultipleSelectInputField from "./formElements/MultipleSelectInputField";
import { countries } from "./formLayouts/countries";
import { interests } from "./formLayouts/interests";
import CountryCodeSelect from "./formElements/CountryCodeSelect";
import { filterConstants } from "../../constants/AppConstants";

export interface AddMemberFormProps {
  initialFormData: AddMemberFormDataUpdated;
  type: "Add" | "Update";
}

export interface AddMemberFormDataUpdated {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  gender: string;
  dob_date: string;
  zip: string;
  country_of_residence: string;
  storecountrycode: string;
  customer_status: string;
  currentSlab: string;
  shipping_address: string;
  delivery_city: string;
  salutation: string;
  subscr_for_news: boolean;
  subscr_for_mkt: boolean;
  email_marketing: boolean;
  heard_about_us: string;
  occupation: string;
  unit_no: string;
  street_address: string;
  is_birthday_month: boolean;
  isinternalstaffdiamo: string;
  isinternstaffgold: boolean | string;
  isinternalsatffquit: boolean | string;
  source_tag: string;
  member_type: string;
  password: string;
  source?: string;
  loyalty_type?: string;
  interest: string;
  isInternalStaff: boolean | string;
  emailNotification: false;
  pushNotification: false;
  other_heard_about_us: string;
  iuNumber1: string;
  iuNumber2: string;
  licensePlate1: string;
  licensePlate2: string;
}

const AddMember: React.FC<AddMemberFormProps> = (props: AddMemberFormProps) => {
  const { initialFormData, type } = props;

  const theme = useTheme();
  const navigate = useNavigate();

  const formStyles = useFormStyles();
  const buttonStyles = useButtonStyles();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [clearForm, setClearForm] = useState(false);
  const [memberFormState, setMemberFormState] =
    useState<AddMemberFormDataUpdated>({
      ...initialFormData,
    });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [countryCode, setCountryCode] = useState<string>("");
  const [countryOptions, setCountryOptions] = useState<string[]>(countries);
  const [errorMessage, setErrorMessage] = useState<string>(
    "Something went wrong!",
  );
  const [emailOfTheUser, setEmailOfTheUser] = useState("");
  const isCountryOfResidenceRequired =
    memberFormState?.storecountrycode !== "SG (+65)";

  const countryOfResidenceValue =
    memberFormState.storecountrycode === "SG (+65)"
      ? "Singapore"
      : memberFormState.country_of_residence;

  // Default CC to SG (+65) when country is Singapore or CC is blank
  const countryCodeAuto =
    memberFormState.country_of_residence === "Singapore"
      ? "SG (+65)"
      : memberFormState.storecountrycode || "SG (+65)";

  const isSingaporeMobile =
    memberFormState.storecountrycode === "SG (+65)" ||
    memberFormState.country_of_residence === "Singapore";

  const isPostalCodeRequired =
    memberFormState?.storecountrycode === "SG (+65)" ||
    memberFormState?.country_of_residence === "Singapore";
  const handleCloseModal = () => {
    setShowModal(false);
  };
  const formatDate = (joinDate: Date) => {
    return (
      joinDate.getFullYear() +
      "-" +
      joinDate.getMonth() +
      "-" +
      joinDate.getDate()
    );
  };
  const formatDOBDate = (date: string) => {
    const dobDate = new Date(date);
    return (
      dobDate.getFullYear() +
      "-" +
      (dobDate.getMonth() + 1) +
      "-" +
      dobDate.getDate()
    );
  };
  const submitHandler = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await uploadData();
      setMemberFormState(initialFormValuesNew);
      setClearForm(true);
      setTimeout(() => {
        setClearForm(false);
      }, 2000);
    } catch (error) {
      setClearForm(false);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  function getMaxDate() {
    const currentDate = new Date();
    currentDate.setFullYear(currentDate.getFullYear() - 18);
    return currentDate.toISOString().split("T")[0];
  }
  const checkDisableMobileSubmit = () => {
    // Mobile validation - if mobile has value, check it doesn't start with 65 and is not all zeros

    if (isSingaporeMobile) {
      const mobileRegex = /^(?!65)(?:\+?\d{8,15}|\d{16})$/;
      const isAllZeros = /^0+$/.test(memberFormState.mobile);
      const isMobileValid =
        !memberFormState.mobile ||
        (mobileRegex.test(memberFormState.mobile) && !isAllZeros);

      const isFormValid = isMobileValid;

      return !isFormValid;
    } else {
      const isAllZeros = /^0+$/.test(memberFormState.mobile);
      return isAllZeros;
    }
  };
  const updateFormValidity = () => {
    const isFirstNameValid = /^[A-Za-z\s]+$/.test(
      memberFormState.first_name.trim(),
    );
    const isLastNameValid = /^[A-Za-z\s]+$/.test(
      memberFormState.last_name.trim(),
    );
    const isGenderSelected = !!memberFormState.gender;

    // Mobile validation - if mobile has value, check it doesn't start with 65 and is not all zeros
    const isMobileValid = !checkDisableMobileSubmit();

    const isFormValid =
      isFirstNameValid && isLastNameValid && isGenderSelected && isMobileValid;

    setDisableSubmit(!isFormValid);
  };

  useEffect(() => {
    updateFormValidity();
  }, [memberFormState]);

  const uploadData = async () => {
    setLoading(true);
    try {
      const payload: any = memberFormState;
      //
      payload.source = "INSTORE";
      payload.loyaltyType = "Loyalty";
      payload.dob_date = formatDOBDate(memberFormState?.dob_date);
      payload.password = "Abcd@123";
      payload.memberType = "Shopper";
      payload.interest = payload.interest.toString();
      payload.heard_about_us =
        memberFormState?.other_heard_about_us !== ""
          ? memberFormState?.other_heard_about_us
          : memberFormState.heard_about_us;
      const countryCodeMatch =
        memberFormState.storecountrycode.match(/^([A-Z]+)/);
      const countryCodeAbbreviation = countryCodeMatch
        ? countryCodeMatch[0]
        : "";

      payload.storecountrycode = countryCodeAbbreviation;
      if (payload.tier === "Gold") {
        payload.isinternstaffgold = "YES";
        // payload.isInternalStaff = "Gold";
        payload.currentSlab = "Gold";
        payload.isinternalstaffdiamo = "NO";
      } else if (payload.tier === "Diamond") {
        payload.isinternalstaffdiamo = "YES";
        // payload.isInternalStaff = "Diamond";
        payload.currentSlab = "Diamond";
        payload.isinternstaffgold = "NO";
      } else {
        payload.isinternstaffgold = "NO";
        payload.isinternalstaffdiamo = "NO";
      }

      // payload.mobile = payload.storecountrycode + memberFormState?.mobile;

      payload.mobile = memberFormState?.mobile;

      if (
        payload.mobile === payload.storecountrycode ||
        payload.mobile === ""
      ) {
        delete payload.mobile;
      }
      const payloadKeys = Object.keys(payload);
      let vehicle1: any = {};
      let vehicle2: any = {};
      payloadKeys.map((key: any) => {
        if (key === "iuNumber1") {
          if (payload[key].length > 0) vehicle1["iuNumber"] = payload[key];
        } else if (key === "iuNumber2") {
          if (payload[key].length > 0) vehicle2["iuNumber"] = payload[key];
        } else if (key === "licensePlate1") {
          if (payload[key].length > 0) vehicle1["licensePlate"] = payload[key];
        } else if (key === "licensePlate2") {
          if (payload[key].length > 0) vehicle2["licensePlate"] = payload[key];
        }
      });
      let vehicleDetails = [];
      if (Object.keys(vehicle1).length) vehicleDetails.push(vehicle1);
      if (Object.keys(vehicle2).length) vehicleDetails.push(vehicle2);
      payload.vehicleDetails = vehicleDetails;
      delete payload.iuNumber1;
      delete payload.iuNumber2;
      delete payload.licensePlate1;
      delete payload.licensePlate2;

      setEmailOfTheUser(payload.email);
      const axiosResponse = await axios.post(
        process.env.REACT_APP_API_URL + APIConstants.customer,
        payload,
      );
      setUploadError(false);
      setTimeout(() => {
        navigate("/members");
      }, 5000);
    } catch (error: any) {
      console.log(error, "erroe");
      if (error?.response?.data?.error) {
        setShowModal(true);
        setUploadError(true);
        setErrorMessage(error?.response?.data?.error);
      }
      setUploadError(true);
    } finally {
      setLoading(false);
      setShowModal(true);
    }
  };

  const searchComponent = (
    <Autocomplete
      size="small"
      value={memberFormState.country_of_residence}
      onChange={(e, newValue) =>
        setMemberFormState((prevState) => ({
          ...prevState,
          country_of_residence: newValue || "",
        }))
      }
      options={countryOptions}
      getOptionLabel={(option) => option}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          required={isCountryOfResidenceRequired}
        />
      )}
    />
  );

  useEffect(() => {
    if (memberFormState.storecountrycode === "SG (+65)") {
      setMemberFormState((prevState) => ({
        ...prevState,
        country_of_residence: "Singapore",
      }));
    }
  }, [memberFormState.storecountrycode]);

  useEffect(() => {
    if (memberFormState.country_of_residence === "Singapore") {
      setMemberFormState((prevState) => ({
        ...prevState,
        storecountrycode: "SG (+65)",
      }));
    }
  }, [memberFormState.country_of_residence]);

  return (
    <Grid
      container
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      width={"100%"}
    >
      <Card
        raised={true}
        variant="outlined"
        sx={{
          mx: { xs: 0, md: 0, lg: 8, xl: 8 },
          borderRadius: "8px",
          p: 4,
          my: 2,
        }}
      >
        {type === "Add" ? (
          <Grid
            display={"flex"}
            justifyContent={"left"}
            alignItems={"center"}
            container
            height={"3rem"}
            marginLeft={-2}
            px={4}
            gap={1}
            sx={{
              backgroundColor: theme?.color?.background,
              "&:hover:not(.Mui-disabled)": {
                cursor: "pointer",
              },
            }}
          >
            <Link to={"/members"}>
              <ArrowBackIosIcon fontSize="small" />
            </Link>
            <Typography
              variant="h6"
              sx={{ color: theme?.color?.primary, fontWeight: "600" }}
            >
              Add Member
            </Typography>
          </Grid>
        ) : null}
        <Grid
          component={"form"}
          onSubmit={submitHandler}
          container
          item
          py={3}
          rowSpacing={2}
        >
          <Grid item width={"100%"}>
            <SplitLayout columns={[4, 4, 4]}>
              <BasicTextField
                setState={setMemberFormState}
                placeholder=""
                label="First Name *"
                stateKey="first_name"
                variant="outlined"
                required={true}
                disableSubmit={setDisableSubmit}
                defaultValue={memberFormState?.first_name}
                clear={clearForm}
              />
              <BasicTextField
                setState={setMemberFormState}
                placeholder=""
                label="Last Name *"
                stateKey="last_name"
                variant="outlined"
                required={true}
                disableSubmit={setDisableSubmit}
                defaultValue={memberFormState?.last_name}
                clear={clearForm}
              />
              <BasicTextField
                setState={setMemberFormState}
                placeholder=""
                stateKey="email"
                variant="outlined"
                label="Email *"
                align="left"
                required={true}
                disableSubmit={setDisableSubmit}
                defaultValue={memberFormState?.email}
                clear={clearForm}
              />
            </SplitLayout>
          </Grid>
          <Grid item width={"100%"}>
            <SplitLayout columns={[4, 4, 4]}>
              <DateField
                setState={setMemberFormState}
                stateKey="dob_date"
                label="Date of Birth *"
                disableSubmit={setDisableSubmit}
                required={true}
                clear={clearForm}
                maxValue={getMaxDate()}
                defaultValue={memberFormState?.dob_date}
              />
              <SelectInputField
                defaultValue={memberFormState?.gender}
                setState={setMemberFormState}
                stateKey="gender"
                label="Gender *"
                options={["Male", "Female", "Other"]}
                align="left"
              />

              <FormLabel
                sx={{
                  ...formStyles.formLabel,
                  textAlign: "left",
                }}
              >
                Postal Code{isPostalCodeRequired ? " *" : ""}
                <BasicTextField
                  setState={setMemberFormState}
                  stateKey="zip"
                  variant="outlined"
                  placeholder=""
                  type="number"
                  required={
                    memberFormState.storecountrycode === "SG (+65)" ||
                    memberFormState?.country_of_residence === "Singapore"
                  }
                  defaultValue={memberFormState?.zip}
                  clear={clearForm}
                  disableSubmit={setDisableSubmit}
                />
              </FormLabel>
            </SplitLayout>
          </Grid>

          <Grid item width={"100%"}>
            <SplitLayout columns={[1.8, 2.6, 4, 4]}>
              <CountryCodeSelect
                label="CC"
                defaultValue={countryCodeAuto}
                stateKey="storecountrycode"
                align="left"
                setState={setMemberFormState}
              />

              <BasicTextField
                setState={setMemberFormState}
                placeholder=""
                stateKey="mobile"
                variant="outlined"
                label="Mobile"
                align="left"
                type="number"
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                defaultValue={memberFormState?.mobile}
                disableSubmit={setDisableSubmit}
                clear={clearForm}
                isSingapore={isSingaporeMobile}
                storeCountryCode={memberFormState.storecountrycode}
                countryOfResidence={memberFormState.country_of_residence}
                applyStrictMobileValidation={true}
                helperTextInfo="*Enter valid mobile number without country code"
                disabled={
                  !memberFormState.storecountrycode &&
                  !memberFormState.country_of_residence
                }
              />
              <FormLabel
                sx={{
                  ...formStyles.formLabel,
                  textAlign: "left",
                }}
              >
                Country {isCountryOfResidenceRequired ? " *" : ""}
                <SelectInputField
                  defaultValue={countryOfResidenceValue}
                  setState={setMemberFormState}
                  stateKey="country_of_residence"
                  options={countries}
                  searchComponent={searchComponent}
                  align="left"
                  required={isCountryOfResidenceRequired}
                />
              </FormLabel>
            </SplitLayout>
          </Grid>
          <SplitLayout columns={[4, 4, 4]}>
            <SelectInputField
              defaultValue={
                memberFormState.isinternstaffgold === "YES"
                  ? "YES"
                  : memberFormState.isinternalstaffdiamo === "YES"
                    ? "YES"
                    : filterConstants.Select
              }
              setState={(newValue: any) => {
                setMemberFormState(newValue);
                if (newValue === "Gold") {
                  setMemberFormState((prevState) => ({
                    ...prevState,
                    isinternstaffgold: "YES",
                    isinternalstaffdiamo: "NO",
                  }));
                }
                if (newValue === "Diamond") {
                  setMemberFormState((prevState) => ({
                    ...prevState,
                    isinternalstaffdiamo: "YES",
                    isinternstaffgold: "NO",
                  }));
                }
              }}
              stateKey="tier"
              label="Is Internal Staff"
              options={["Select", "Gold", "Diamond"]}
              align="left"
            />
          </SplitLayout>
          <Grid item width={"100%"}>
            <SplitLayout columns={[4, 4, 4]}>
              <SelectInputField
                defaultValue={memberFormState?.customer_status}
                setState={setMemberFormState}
                stateKey="customer_status"
                label="Member Status"
                options={[filterConstants.Active, filterConstants.Inactive]}
                align="left"
              />
              <MultipleSelectInputField
                defaultValue={memberFormState?.interest}
                setState={setMemberFormState}
                stateKey="interest"
                label="Interest"
                options={interests}
                align="left"
              />
            </SplitLayout>
          </Grid>

          <SplitLayout columns={[4, 4, 4]}>
            <SelectInputField
              defaultValue={memberFormState?.heard_about_us}
              setState={setMemberFormState}
              label="Where did you hear about us?"
              stateKey="heard_about_us"
              options={[
                "Through friends and family",
                "Advertisements",
                "Social Media",
                "I work around/at Suntec City",
                "Other",
              ]}
              align="left"
            />
            {memberFormState?.heard_about_us === "Other" && (
              <BasicTextField
                setState={setMemberFormState}
                stateKey="other_heard_about_us"
                label="Other *"
                variant="outlined"
                placeholder=""
                type="string"
                required={true}
                defaultValue={memberFormState?.other_heard_about_us}
                clear={clearForm}
              />
            )}
          </SplitLayout>
          <SplitLayout columns={[4, 4, 4]}>
            <BasicTextField
              setState={setMemberFormState}
              placeholder=""
              stateKey="iuNumber1"
              variant="outlined"
              label="IU Number 1"
              align="left"
              defaultValue={""}
              disableSubmit={setDisableSubmit}
            />
            <BasicTextField
              setState={setMemberFormState}
              placeholder=""
              stateKey="licensePlate1"
              required={memberFormState.iuNumber1 !== ""}
              variant="outlined"
              label="License Plate 1"
              align="left"
              defaultValue={""}
            />
          </SplitLayout>
          <SplitLayout columns={[4, 4, 4]}>
            <BasicTextField
              setState={setMemberFormState}
              placeholder=""
              stateKey="iuNumber2"
              variant="outlined"
              label="IU Number 2"
              align="left"
              disableSubmit={setDisableSubmit}
              defaultValue={""}
            />
            <BasicTextField
              setState={setMemberFormState}
              placeholder=""
              stateKey="licensePlate2"
              variant="outlined"
              label="License Plate 2"
              align="left"
              disableSubmit={setDisableSubmit}
              defaultValue={""}
              required={memberFormState.iuNumber2 !== ""}
            />
          </SplitLayout>
          <Grid
            item
            width={"100%"}
            pt={2}
            pb={2}
            borderBottom={theme.border?.smallGrey}
          >
            <SplitLayout columns={[6]}>
              <Grid>
                <FormLabel
                  sx={{
                    ...formStyles.formLabel,
                    textAlign: "left",
                  }}
                >
                  Communication Preferences:
                </FormLabel>
                <CheckBoxSection
                  setState={setMemberFormState}
                  direction="column"
                  items={[
                    {
                      stateKey: "subscr_for_news",
                      value: "Email",
                      checked: memberFormState?.subscr_for_news,
                    },
                    {
                      stateKey: "subscr_for_mkt",
                      value: "Push Notification",
                      checked: memberFormState?.subscr_for_mkt,
                    },
                  ]}
                />
              </Grid>
            </SplitLayout>
          </Grid>

          <Grid item container width={"100%"}>
            <Grid item width={"100%"} display={"flex"} justifyContent={"end"}>
              <Button
                component={Link}
                to={"/members"}
                sx={{
                  ...buttonStyles.outlinedSecondary,
                  width: "25%",
                  mr: 4,
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={disableSubmit || !Boolean(memberFormState?.dob_date)}
                type="submit"
                sx={{
                  ...buttonStyles.containedSecondary,
                  width: "25%",
                  marginRight: "1rem",
                }}
                variant="contained"
              >
                {type} Member
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {loading && <BasicModal content={<Loader />} />}
        {showModal && (
          <BasicModal
            title={uploadError ? errorMessage : "Member Added Successfully!"}
            success={!uploadError}
            setState={setShowModal}
            reference={emailOfTheUser}
            content={loading ? <Loader /> : undefined}
          />
        )}
      </Card>
    </Grid>
  );
};

export default AddMember;
